import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_COMPANIES,
  RECEIVING_COMPANIES,
  RECEIVE_COMPANY_METADATA,
  RECEIVE_BANKS,
  RECEIVE_ADDRESSES,
  SAVING_COMPANY,
  UPDATING_COMPANY,
  DELETE_COMPANY,
  DELETING_COMPANY,
  CLEAR_COMPANY_STATE,
  CLEAR_STATE,
  RESET_COMPANY_META,
  INCREMENT_COMPANY_COUNT,
  SET_ALL_COMPANIES_LOADED
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrlWithQuery } from '@/utils/api'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const bankSchema = new schema.Entity('banks')
const addressSchema = new schema.Entity('addresses')
const companySchema = new schema.Entity('companies', { bank: bankSchema, address: addressSchema })
const companyListSchema = new schema.Array(companySchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null,
  allCompaniesLoaded: false
}

const actions = {
  fetchCompaniesWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_COMPANIES, true)
    return http.get(buildUrlWithQuery('companies', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_COMPANIES, false)
        this.commit(RESET_COMPANY_META)
        if (!(r && r.results)) return Promise.reject('Error fetching companies')
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, companyListSchema)
        if (entities && entities.addresses) {
          this.commit(RECEIVE_ADDRESSES, { data: entities.addresses })
        }
        if (entities && entities.banks) {
          this.commit(RECEIVE_BANKS, { data: entities.banks })
        }
        this.commit(RECEIVE_COMPANIES, { data: entities.companies })
        this.commit(RECEIVE_COMPANY_METADATA, { next, previous, count, result: result.length })
        return { query, count, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_COMPANIES, false)
        return e
      })
  },
  setCompanyPreloadStatus (store, payload) {
    this.commit(SET_ALL_COMPANIES_LOADED, payload)
  },
  clearCompanyState (store, clearCache = false) {
    return this.commit(CLEAR_COMPANY_STATE, clearCache)
  }
}

const getters = {
  getCompanies: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAllCompanies: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getCompanyById: (state, rootState) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getCompanyByBatchId: (state, rootState) => (batchId) => {
    if (!batchId) return null
    const batch = rootState.getBatchById(batchId)
    if (!(batch && batch.company)) return []
    if (state.data && state.data[batch.company]) {
      return state.data[batch.company]
    } else if (state.cache && state.cache[batch.company]) {
      return state.cache[batch.company]
    } else {
      return []
    }
  },
  getDefaultCompany: (state) => {
    return state.cache ? Object.values(state.cache).find(v => v.active) : {}
  },
  getCompanyMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getCompanyFetchStatus: (state) => {
    return state.receivingData
  },
  getCompanySaveStatus: (state) => {
    return state.saving
  },
  getCompanyUpdateStatus: (state) => {
    return state.updating
  },
  getCompanyDeleteStatus: (state) => {
    return state.deleting
  },
  getCompanyPreloadStatus: (state) => {
    return state.allCompaniesLoaded
  }
}

const mutations = {
  [RECEIVE_COMPANIES] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_COMPANIES] (state, payload) {
    state.receivingData = payload
  },
  [SAVING_COMPANY] (state, payload) {
    state.saving = payload
  },
  [UPDATING_COMPANY] (state, payload) {
    state.updating = payload
  },
  [DELETE_COMPANY] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_COMPANY] (state, payload) {
    state.deleting = payload
  },
  [INCREMENT_COMPANY_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_COMPANY_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_COMPANY_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_COMPANY_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  },
  [SET_ALL_COMPANIES_LOADED] (state, payload) {
    state.allCompaniesLoaded = payload
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
